import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { purchaseApi, inventoryApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * FR-PUR-05.01 / 02 / 03 — Requisitions.
 * List/detail/create with submit, approve, convert-to-PO actions.
 */
export function RequisitionsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showConvert, setShowConvert] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['purchase', 'requisitions', statusFilter],
    queryFn: () => purchaseApi.listRequisitions({ status: statusFilter || undefined }),
  });

  const kpis = (() => {
    const rows = listQuery.data ?? [];
    let pending = 0, approved = 0, totalValue = 0;
    for (const r of rows) {
      if (r.status === 'pending_approval') pending++;
      if (r.status === 'approved') approved++;
      totalValue += parseFloat(r.estimated_total) || 0;
    }
    return { total: rows.length, pending, approved, totalValue };
  })();

  const STATUS_OPTIONS = [
    { value: '',                 label: 'All' },
    { value: 'draft',            label: 'Draft' },
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved',         label: 'Approved' },
    { value: 'converted',        label: 'Converted' },
    { value: 'rejected',         label: 'Rejected' },
    { value: 'cancelled',        label: 'Cancelled' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Purchase"
        title="Requisitions"
        subtitle="Internal purchase requests — submit, approve, then convert approved requisitions into purchase orders."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New requisition
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="In view" value={kpis.total} />
        <KpiTile label="Pending approval" value={kpis.pending}
          accent={kpis.pending > 0 ? 'amber' : 'default'} />
        <KpiTile label="Ready to convert" value={kpis.approved}
          accent={kpis.approved > 0 ? 'sage' : 'default'} />
        <KpiTile label="Estimated value" value={`${fmtCurrency(kpis.totalValue)}`} />
      </div>

      {/* Status chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap ${
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_32rem] gap-6">
        <div className="card overflow-hidden">
          {listQuery.isLoading ? <LoadingState rows={5} /> : listQuery.error ? <ErrorState error={listQuery.error} /> :
            !listQuery.data?.length ? (
              <EmptyState
                message={statusFilter ? `No ${statusFilter.replace('_', ' ')} requisitions` : 'No requisitions yet'}
                hint={statusFilter ? 'Try a different filter.' : 'Create the first requisition to begin a purchase request.'}
                action={!statusFilter && (
                  <button className="btn-primary" onClick={() => setShowCreate(true)}>
                    + New requisition
                  </button>
                )}
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Req #</th>
                      <th>Requester</th>
                      <th>Status</th>
                      <th className="text-right">Est. value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(listQuery.data ?? []).map((r: any) => (
                      <tr key={r.id}
                          onClick={() => setSelected(r.id)}
                          className={`cursor-pointer ${selected === r.id ? '!bg-canvas-subtle' : ''}`}>
                        <td className="font-mono text-xs font-semibold text-ink-900">{r.req_number}</td>
                        <td className="text-sm text-ink-700">{r.requester_name}</td>
                        <td><StatusBadge status={r.status} /></td>
                        <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                          ${parseFloat(r.estimated_total).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>

        <div className="card p-5 min-h-[400px]">
          {!selected ? (
            <EmptyState
              message="Select a requisition"
              hint="Click a row on the left to view items and lifecycle actions."
            />
          ) : (
            <ReqDetail
              reqId={selected}
              onChanged={() => qc.invalidateQueries({ queryKey: ['purchase', 'requisitions'] })}
              onConvert={(id) => setShowConvert(id)}
            />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateReqModal onClose={() => setShowCreate(false)}
          onCreated={(id) => {
            qc.invalidateQueries({ queryKey: ['purchase', 'requisitions'] });
            setShowCreate(false); setSelected(id);
          }}
        />
      )}

      {showConvert && (
        <ConvertReqModal
          reqId={showConvert}
          onClose={() => setShowConvert(null)}
          onConverted={() => {
            qc.invalidateQueries({ queryKey: ['purchase', 'requisitions'] });
            setShowConvert(null);
          }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    draft:            'bg-canvas-subtle text-ink-700 ring-ink-200',
    pending_approval: 'bg-amber-50 text-amber-800 ring-amber-200',
    approved:         'bg-sage-50 text-sage-700 ring-sage-200',
    converted:        'bg-blue-50 text-blue-700 ring-blue-200',
    rejected:         'bg-red-50 text-red-700 ring-red-200',
    cancelled:        'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function ReqDetail({ reqId, onChanged, onConvert }:
    { reqId: string; onChanged: () => void; onConvert: (id: string) => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['purchase', 'requisition', reqId],
                       queryFn: () => purchaseApi.getRequisition(reqId) });

  const [showCancel, setShowCancel] = useState(false);
  const [showReject, setShowReject] = useState(false);

  const submit = useMutation({
    mutationFn: () => purchaseApi.submitRequisition(reqId),
    onSuccess: () => { toast.success('Submitted for approval'); qc.invalidateQueries({ queryKey: ['purchase', 'requisition', reqId] }); onChanged(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const decide = useMutation({
    mutationFn: ({ approve, reason }: any) => purchaseApi.decideRequisition(reqId, approve, reason),
    onSuccess: (d: any) => {
      toast.success(d.rejected ? 'Rejected' : 'Approved');
      qc.invalidateQueries({ queryKey: ['purchase', 'requisition', reqId] });
      onChanged();
      setShowReject(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => purchaseApi.cancelRequisition(reqId, reason),
    onSuccess: () => {
      toast.success('Cancelled');
      qc.invalidateQueries({ queryKey: ['purchase', 'requisition', reqId] });
      onChanged();
      setShowCancel(false);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} />;
  const r = q.data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="font-mono text-xs text-ink-500 mb-0.5">{r.req_number}</div>
          <div className="font-display font-bold text-lg text-ink-900 truncate">{r.requester_name}</div>
          {r.department && <div className="text-xs text-ink-500 mt-1">Dept · {r.department}</div>}
          {r.needed_by && (
            <div className="text-xs text-ink-500 mt-0.5">
              Needed by {new Date(r.needed_by).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
            </div>
          )}
          {r.justification && (
            <div className="text-xs text-ink-500 italic mt-1 leading-relaxed">{r.justification}</div>
          )}
        </div>
        <StatusBadge status={r.status} />
      </div>

      {/* Alerts */}
      {r.rejected_at && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700 mb-3">
          <div className="font-semibold">Rejected</div>
          <div className="mt-0.5">{new Date(r.rejected_at).toLocaleString()} · {r.rejected_by_name}</div>
          {r.rejection_reason && <div className="mt-1">{r.rejection_reason}</div>}
        </div>
      )}
      {r.converted_po_number && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl px-3 py-2 text-xs text-sage-800 mb-3">
          Converted to PO <span className="font-mono font-semibold">{r.converted_po_number}</span>
        </div>
      )}

      {/* Line items */}
      <div className="border border-ink-100 rounded-xl overflow-hidden mb-4">
        <table className="table text-xs">
          <thead>
            <tr>
              <th className="w-10">#</th>
              <th>Description</th>
              <th className="text-right">Qty</th>
              <th className="text-right">Est. price</th>
              <th>Preferred supplier</th>
            </tr>
          </thead>
          <tbody>
            {r.lines.map((l: any) => (
              <tr key={l.id}>
                <td className="font-mono text-2xs text-ink-500">{l.line_no}</td>
                <td>
                  <div className="font-medium text-ink-900">{l.description}</div>
                  {l.product_name && (
                    <div className="font-mono text-2xs text-ink-500 mt-0.5">
                      {l.sku} — {l.product_name}
                    </div>
                  )}
                </td>
                <td className="text-right font-mono tabular-nums">{parseFloat(l.quantity).toFixed(0)}</td>
                <td className="text-right font-mono tabular-nums">{fmtCurrency(parseFloat(l.estimated_unit_price))}</td>
                <td className="text-xs text-ink-600">
                  {l.preferred_supplier_name ?? <span className="text-ink-400">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total */}
      <div className="bg-canvas-subtle rounded-xl p-4 mb-4">
        <div className="flex justify-between items-baseline">
          <span className="font-semibold text-ink-900">Estimated total</span>
          <span className="font-display font-bold text-2xl text-ink-900 tabular-nums tracking-tight">
            ${parseFloat(r.estimated_total).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Workflow actions */}
      <div className="flex gap-2 justify-end flex-wrap">
        {r.status === 'draft' && (
          <>
            <button className="btn-secondary" disabled={cancel.isPending}
              onClick={() => setShowCancel(true)}>Cancel</button>
            <button className="btn-primary" disabled={submit.isPending} onClick={() => submit.mutate()}>
              {submit.isPending ? 'Submitting…' : 'Submit'}
            </button>
          </>
        )}
        {r.status === 'pending_approval' && (
          <>
            <button className="btn-secondary" disabled={decide.isPending}
              onClick={() => setShowReject(true)}>Reject</button>
            <button className="btn-primary" disabled={decide.isPending}
              onClick={() => decide.mutate({ approve: true })}>
              {decide.isPending ? 'Approving…' : 'Approve'}
            </button>
          </>
        )}
        {r.status === 'approved' && (
          <button className="btn-primary" onClick={() => onConvert(reqId)}>Convert to PO</button>
        )}
      </div>

      {showCancel && (
        <CancelReqDialog
          reqNumber={r.req_number}
          loading={cancel.isPending}
          onClose={() => setShowCancel(false)}
          onConfirm={(reason) => cancel.mutate(reason)}
        />
      )}
      {showReject && (
        <RejectReqDialog
          reqNumber={r.req_number}
          loading={decide.isPending}
          onClose={() => setShowReject(false)}
          onConfirm={(reason) => decide.mutate({ approve: false, reason })}
        />
      )}
    </div>
  );
}

function CancelReqDialog({
  reqNumber, loading, onClose, onConfirm,
}: { reqNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Purchase · Cancel</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Cancel requisition?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Cancel <span className="font-mono font-semibold text-ink-900">{reqNumber}</span>?
          The requisition stays on record for audit but cannot be reopened.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="text-danger">*</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. No longer needed" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Keep it</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={!reason.trim() || loading}
          onClick={() => onConfirm(reason.trim())}>
          {loading ? 'Cancelling…' : 'Cancel requisition'}
        </button>
      </div>
    </Modal>
  );
}

function RejectReqDialog({
  reqNumber, loading, onClose, onConfirm,
}: { reqNumber: string; loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Purchase · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject requisition?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Reject <span className="font-mono font-semibold text-ink-900">{reqNumber}</span>?
          The requisition moves back to the requester. Your reason is visible to them.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={3} value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Budget not approved" />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={() => onConfirm(reason.trim() || undefined)}>
          {loading ? 'Rejecting…' : 'Reject requisition'}
        </button>
      </div>
    </Modal>
  );
}

function CreateReqModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [department, setDepartment] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [justification, setJustification] = useState('');
  const [lines, setLines] = useState<Array<any>>([
    { description: '', quantity: 1, estimatedUnitPrice: 0 },
  ]);

  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });

  const mut = useMutation({
    mutationFn: () => purchaseApi.createRequisition({
      department: department || undefined,
      neededBy: neededBy || undefined,
      justification: justification || undefined,
      lines: lines.filter((l) => l.description.trim()),
    }),
    onSuccess: (d) => { toast.success(`Requisition ${d.reqNumber} created as draft`); onCreated(d.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · New</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New requisition</h2>
        <p className="text-sm text-ink-500 mt-1">
          Draft a request for procurement. Once submitted it will route for approval before becoming a PO.
        </p>
      </div>

      <div className="px-6 pt-4 pb-3 grid grid-cols-2 gap-3 flex-shrink-0">
        <div>
          <label className="label">Department</label>
          <input className="input" value={department} onChange={(e) => setDepartment(e.target.value)}
            placeholder="Operations" />
        </div>
        <div>
          <label className="label">Needed by</label>
          <input type="date" className="input" value={neededBy} onChange={(e) => setNeededBy(e.target.value)} />
        </div>
      </div>

      <div className="px-6 pb-3 flex-shrink-0">
        <label className="label">Justification <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea className="input" rows={2} value={justification} onChange={(e) => setJustification(e.target.value)}
          placeholder="Why the team needs this" />
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="eyebrow text-ink-500 mb-2">Lines</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th className="text-right w-20">Qty</th>
                <th className="text-right w-28">Est. price</th>
                <th>Preferred supplier</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>
                    <input className="input text-xs" value={l.description}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, description: e.target.value } : x))}
                      placeholder="e.g. 4× stainless bowls, food-safe" />
                  </td>
                  <td className="text-right">
                    <input type="number" min={0.01} step={1}
                      className="input w-20 text-right tabular-nums" value={l.quantity}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td className="text-right">
                    <input type="number" min={0} step={0.01}
                      className="input w-24 text-right tabular-nums" value={l.estimatedUnitPrice}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, estimatedUnitPrice: parseFloat(e.target.value) || 0 } : x))} />
                  </td>
                  <td>
                    <select className="input text-xs" value={l.preferredSupplierId ?? ''}
                      onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, preferredSupplierId: e.target.value || null } : x))}>
                      <option value="">—</option>
                      {supQuery.data?.map((s: any) => <option key={s.id} value={s.id}>{s.legal_name}</option>)}
                    </select>
                  </td>
                  <td>
                    <button
                      className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                      onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                      aria-label="Remove line"
                    >✕</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          className="mt-2 text-sm font-medium text-ink-700 hover:text-ink-900 transition-colors"
          onClick={() => setLines((prev) => [...prev, { description: '', quantity: 1, estimatedUnitPrice: 0 }])}
        >
          + Add line
        </button>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create draft'}
        </button>
      </div>
    </Modal>
  );
}

function ConvertReqModal({ reqId, onClose, onConverted }:
    { reqId: string; onClose: () => void; onConverted: () => void }) {
  const q = useQuery({ queryKey: ['purchase', 'requisition', reqId], queryFn: () => purchaseApi.getRequisition(reqId) });
  const supQuery = useQuery({ queryKey: ['suppliers'], queryFn: () => purchaseApi.listSuppliers() });
  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });

  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [requiredDate, setRequiredDate] = useState('');
  const [lineOverrides, setLineOverrides] = useState<Record<string, {
    productId: string; quantity: number; unitPrice: number; taxRate: number;
  }>>({});

  // Pre-populate line overrides when requisition data loads
  if (q.data && Object.keys(lineOverrides).length === 0 && q.data.lines.length > 0) {
    const init: any = {};
    for (const l of q.data.lines) {
      init[l.id] = {
        productId: l.product_id ?? '',
        quantity: parseFloat(l.quantity),
        unitPrice: parseFloat(l.estimated_unit_price),
        taxRate: 0,
      };
    }
    setLineOverrides(init);
  }

  const mut = useMutation({
    mutationFn: () => purchaseApi.convertRequisition(reqId, {
      supplierId, warehouseId,
      requiredDate: requiredDate || undefined,
      lineOverrides: Object.entries(lineOverrides).map(([lineId, override]) => ({
        requisitionLineId: lineId, ...override,
      })),
    }),
    onSuccess: (d) => { toast.success(`PO ${d.poNumber} created as draft`); onConverted(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;

  const canSubmit = supplierId && warehouseId &&
    Object.values(lineOverrides).every((l) => l.productId && l.quantity > 0 && l.unitPrice >= 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Purchase · Convert</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Convert to purchase order</h2>
        <p className="text-sm text-ink-500 mt-1">
          Pick a supplier and delivery warehouse, then confirm line pricing. A draft PO is created you can review before submitting.
        </p>
      </div>

      <div className="px-6 pt-4 pb-3 grid grid-cols-3 gap-3 flex-shrink-0">
        <div>
          <label className="label">Supplier <span className="text-danger">*</span></label>
          <select className="input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
            <option value="">Select…</option>
            {supQuery.data?.map((s: any) => <option key={s.id} value={s.id}>{s.legal_name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Warehouse <span className="text-danger">*</span></label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select…</option>
            {whQuery.data?.map((w: any) => <option key={w.id} value={w.id}>{w.code} — {w.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Required by</label>
          <input type="date" className="input" value={requiredDate} onChange={(e) => setRequiredDate(e.target.value)} />
        </div>
      </div>

      <div className="px-6 flex-1 overflow-y-auto min-h-0">
        <div className="eyebrow text-ink-500 mb-2">Line overrides</div>
        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <table className="table">
            <thead>
              <tr>
                <th>Req line</th>
                <th>Product ID <span className="text-danger">*</span></th>
                <th className="text-right w-20">Qty</th>
                <th className="text-right w-24">Unit price</th>
                <th className="text-right w-16">Tax %</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.lines.map((l: any) => {
                const o = lineOverrides[l.id] ?? { productId: '', quantity: 0, unitPrice: 0, taxRate: 0 };
                return (
                  <tr key={l.id}>
                    <td className="text-xs">
                      <div className="font-medium text-ink-900">{l.description}</div>
                      <div className="font-mono text-2xs text-ink-500 mt-0.5">
                        Est: {parseFloat(l.quantity).toFixed(0)} × ${parseFloat(l.estimated_unit_price).toFixed(2)}
                      </div>
                    </td>
                    <td>
                      <input className="input text-xs font-mono" placeholder="product UUID" value={o.productId}
                        onChange={(e) => setLineOverrides((prev) => ({ ...prev, [l.id]: { ...o, productId: e.target.value } }))} />
                    </td>
                    <td className="text-right">
                      <input type="number" min={0.01} step={1}
                        className="input w-20 text-right tabular-nums" value={o.quantity}
                        onChange={(e) => setLineOverrides((prev) => ({ ...prev, [l.id]: { ...o, quantity: parseFloat(e.target.value) || 0 } }))} />
                    </td>
                    <td className="text-right">
                      <input type="number" min={0} step={0.01}
                        className="input w-24 text-right tabular-nums" value={o.unitPrice}
                        onChange={(e) => setLineOverrides((prev) => ({ ...prev, [l.id]: { ...o, unitPrice: parseFloat(e.target.value) || 0 } }))} />
                    </td>
                    <td className="text-right">
                      <input type="number" min={0} max={100} step={0.1}
                        className="input w-16 text-right tabular-nums" value={o.taxRate}
                        onChange={(e) => setLineOverrides((prev) => ({ ...prev, [l.id]: { ...o, taxRate: parseFloat(e.target.value) || 0 } }))} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-500 mt-2 leading-relaxed">
          Tip: paste the product UUID (find it on the product detail URL). A future enhancement would offer a product picker here.
        </p>
      </div>

      <div className="px-6 py-4 mt-3 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!canSubmit || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Converting…' : 'Create PO draft'}
        </button>
      </div>
    </Modal>
  );
}
